function sameSize(a, b) {
	if (a._w !== b._w || a._h !== b._h) {
		throw new Error('Size mismatch');
	}
}

function validate(m) {
	if (m._m.length !== m._w * m._h) {
		throw new Error('Size incorrect');
	}
	if (m._m.filter(isNaN).length) {
		throw new Error('NaN in matrix');
	}
}

function idx(m, i, j) {
	if (!(m instanceof Matrix)) {
		throw new Error('Not a matrix');
	}
	/* OpenGL wants the transpose of how I'd "normally" do this */
	return j * m._h + i;
}

function get(m, i, j) {
	return m._m[idx(m, i, j)];
}

function set(m, i, j, v) {
	m._m[idx(m, i, j)] = v;
}

function inc(m, i, j, v) {
	m._m[idx(m, i, j)] += v;
}

function assertNumbers(...x) {
	if (x.filter(x => typeof x !== 'number').length) {
		throw new Error('Not all arguments are valid numbers');
	}
}

export default class Matrix {

	_m = null;
	_w = 0;
	_h = 0;

	constructor(m, w, h) {
		if (m.length !== w * h) {
			throw new Error('Size mismatch');
		}
		this._m = m;
		this._w = w;
		this._h = h;
		validate(this);
	}

	static createZero(w, h) {
		return new Matrix(Array(w * h).fill(0), w, h);
	}

	static createIdentity(w) {
		const m = Matrix.createZero(w, w);
		for (let i = 0; i < w; i++) {
			set(m, i, i, 1);
		}
		return m;
	}

	clone() {
		return new Matrix([...this._m], this._w, this._h);
	}

	inc(x) {
		sameSize(this, x);
		const m = this._m;
		for (let i = 0; i < m.length; i++) {
			m[i] += x._m[i];
		}
		return this;
	}

	dec(x) {
		sameSize(this, x);
		const m = this._m;
		for (let i = 0; i < m.length; i++) {
			m[i] -= x_m[i];
		}
		return this;
	}

	add(x) {
		return this.clone().inc(x);
	}

	sub(x) {
		return this.clone().dec(x);
	}

	/* Left-multiple other matrix by this one, return result */
	multiply(x) {
		if (this._w !== x._h) {
			throw new Error('Size mismatch');
		}
		const m = this;
		const r = Matrix.createZero(x._w, m._h);
		for (let i = 0; i < m._h; i++) {
			for (let j = 0; j < x._w; j++) {
				for (let k = 0; k < m._w; k++) {
					inc(r, i, j, get(m, i, k) * get(x, k, j));
				}
			}
		}
		return r;
	}

	static createHyper(x, y, z = 0) {
		const m = Matrix.createIdentity(4);
		set(m, 3, 0, x);
		set(m, 3, 1, y);
		set(m, 3, 2, z);
		return m;
	}

	static createTranslate(x, y, z = 0) {
		const m = Matrix.createIdentity(4);
		set(m, 0, 3, x);
		set(m, 1, 3, y);
		set(m, 2, 3, z);
		return m;
	}

	static createScale(x, y, z = 1) {
		const m = Matrix.createIdentity(4);
		set(m, 0, 0, x);
		set(m, 1, 1, y);
		set(m, 2, 2, z);
		return m;
	}

	static createRotate2D(angle) {
		const m = Matrix.createIdentity(4);
		const c = Math.cos(angle);
		const s = Math.sin(angle);
		set(m, 0, 0, c);
		set(m, 1, 0, -s);
		set(m, 0, 1, s);
		set(m, 1, 1, c);
		return m;
	}

	/* Left-multiply self by other matrix */
	transform(x) {
		sameSize(this, x);
		const z = x.multiply(this);
		this._m = z._m;
		this._w = z._w;
		this._h = z._h;
	}

	translateBy(x, y, z = 0) {
		assertNumbers(x, y, z);
		this.transform(Matrix.createTranslate(x, y, z));
	}

	scaleBy(x, y, z = 1) {
		assertNumbers(x, y, z);
		this.transform(Matrix.createScale(x, y, z));
	}

	rotateBy2D(angle) {
		assertNumbers(angle);
		this.transform(Matrix.createRotate2D(angle));
	}

	hyperBy(x, y, z) {
		this.transform(Matrix.createHyper(x, y, z));
	}

}
